/**
 * Small rich text editor (contenteditable + execCommand) for product descriptions, blog posts and richtext fields.
 * Value is HTML; the server sanitizes it again (render/sanitize.go richtext / article policy).
 * mode="article" adds headings, images, code and HTML source; mode="richtext" is the component field subset.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Divider, Input, Space, Tooltip } from 'antd';
import {
    BoldOutlined, ItalicOutlined, UnderlineOutlined, StrikethroughOutlined, OrderedListOutlined, UnorderedListOutlined,
    LinkOutlined, ClearOutlined, CodeOutlined, PictureOutlined, DisconnectOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';

const ALLOWED_RICH = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'A', 'UL', 'OL', 'LI', 'H3', 'H4', 'BLOCKQUOTE', 'CODE', 'MARK', 'SUB', 'SUP', 'SPAN']);
const ALLOWED_ARTICLE = new Set([...ALLOWED_RICH, 'H2', 'H5', 'HR', 'PRE', 'IMG', 'FIGURE', 'FIGCAPTION', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'CAPTION', 'DIV']);
const SAFE_URL = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/** client-side clean-up of pasted/edited HTML (DOMParser; the server has the final say) */
export const cleanHTML = (html, article = false) => {
    const allowed = article ? ALLOWED_ARTICLE : ALLOWED_RICH;
    const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
    const walk = (node) => {
        [...node.childNodes].forEach((ch) => {
            if (ch.nodeType === 8) { ch.remove(); return; }
            if (ch.nodeType !== 1) return;
            if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'META', 'LINK'].includes(ch.tagName)) { ch.remove(); return; }
            walk(ch);
            if (!allowed.has(ch.tagName)) {
                // unwrap unknown tags (DIV → P in richtext)
                if (ch.tagName === 'DIV' && !article) {
                    const p = doc.createElement('p');
                    while (ch.firstChild) p.appendChild(ch.firstChild);
                    ch.replaceWith(p);
                } else {
                    ch.replaceWith(...ch.childNodes);
                }
                return;
            }
            [...ch.attributes].forEach((a) => {
                const n = a.name.toLowerCase();
                const keep = (n === 'href' && ch.tagName === 'A' && SAFE_URL.test(a.value))
                    || (n === 'target' && ch.tagName === 'A')
                    || (article && ch.tagName === 'IMG' && ['src', 'alt', 'width', 'height'].includes(n) && (n !== 'src' || /^(https:|\/)/i.test(a.value)))
                    || (article && ['colspan', 'rowspan'].includes(n));
                if (!keep) ch.removeAttribute(a.name);
            });
        });
    };
    walk(doc.body);
    return doc.body.innerHTML.replace(/<p><br><\/p>$/, '');
};

const RichEditor = ({ value, onChange, mode = 'article', minHeight = 180, placeholder }) => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const last = useRef(value || '');
    const [source, setSource] = useState(false);
    const article = mode === 'article';

    useEffect(() => {
        if (ref.current && !source && (value || '') !== last.current) {
            ref.current.innerHTML = value || '';
            last.current = value || '';
        }
    }, [value, source]);
    useEffect(() => { if (ref.current) ref.current.innerHTML = value || ''; }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

    const emit = () => {
        const html = cleanHTML(ref.current?.innerHTML || '', article);
        last.current = html;
        onChange?.(html);
    };
    const exec = (cmd, arg) => {
        ref.current?.focus();
        document.execCommand(cmd, false, arg);
        emit();
    };
    const link = () => {
        const href = window.prompt(t('Link URL'), 'https://');
        if (href && SAFE_URL.test(href)) exec('createLink', href);
    };
    const image = () => {
        const src = window.prompt(t('Image URL'), 'https://');
        if (src && /^(https:|\/)/i.test(src)) exec('insertImage', src);
    };
    const onPaste = (e) => {
        const html = e.clipboardData?.getData('text/html');
        if (html) {
            e.preventDefault();
            document.execCommand('insertHTML', false, cleanHTML(html, article));
            emit();
        }
    };
    const B = ({ icon, title, onClick, children }) => (
        <Tooltip title={title} mouseEnterDelay={0.4}>
            <Button size="small" type="text" icon={icon} onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={source}>{children}</Button>
        </Tooltip>
    );

    return (
        <div className="yc-rich" style={{ border: '1px solid var(--ant-color-border, #d9d9d9)', borderRadius: 6, background: 'var(--ant-color-bg-container, #fff)' }}>
            <Space size={0} wrap style={{ padding: 4, borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)', width: '100%' }}>
                <B icon={<BoldOutlined />} title={t('Bold')} onClick={() => exec('bold')} />
                <B icon={<ItalicOutlined />} title={t('Italic')} onClick={() => exec('italic')} />
                <B icon={<UnderlineOutlined />} title={t('Underline')} onClick={() => exec('underline')} />
                <B icon={<StrikethroughOutlined />} title={t('Strikethrough')} onClick={() => exec('strikeThrough')} />
                <Divider type="vertical" />
                {article && <B title={t('Heading')} onClick={() => exec('formatBlock', 'H2')}>H2</B>}
                <B title={t('Heading')} onClick={() => exec('formatBlock', 'H3')}>H3</B>
                <B title={t('Paragraph')} onClick={() => exec('formatBlock', 'P')}>¶</B>
                <B title={t('Quote')} onClick={() => exec('formatBlock', 'BLOCKQUOTE')}>❝</B>
                <B icon={<UnorderedListOutlined />} title={t('Bulleted list')} onClick={() => exec('insertUnorderedList')} />
                <B icon={<OrderedListOutlined />} title={t('Numbered list')} onClick={() => exec('insertOrderedList')} />
                <Divider type="vertical" />
                <B icon={<LinkOutlined />} title={t('Link')} onClick={link} />
                <B icon={<DisconnectOutlined />} title={t('Remove link')} onClick={() => exec('unlink')} />
                {article && <B icon={<PictureOutlined />} title={t('Image', { context: 'shop' })} onClick={image} />}
                <B icon={<ClearOutlined />} title={t('Clear formatting')} onClick={() => exec('removeFormat')} />
                {article && (
                    <Tooltip title={t('HTML')}>
                        <Button size="small" type={source ? 'primary' : 'text'} icon={<CodeOutlined />} onClick={() => setSource((s) => !s)} />
                    </Tooltip>
                )}
            </Space>
            {source ? (
                <Input.TextArea value={value || ''} onChange={(e) => { last.current = e.target.value; onChange?.(e.target.value); }}
                    autoSize={{ minRows: 8 }} style={{ fontFamily: 'monospace', border: 0, borderRadius: 0 }}
                    onBlur={() => onChange?.(cleanHTML(value || '', article))} />
            ) : (
                <div
                    ref={ref}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={placeholder || ''}
                    onInput={emit}
                    onBlur={emit}
                    onPaste={onPaste}
                    style={{ minHeight, padding: '8px 12px', outline: 'none', overflowWrap: 'anywhere', lineHeight: 1.6 }}
                />
            )}
        </div>
    );
};

export default RichEditor;
